import { BRDGE_PROJECT_ID, FEATURES } from "@/lib/config";

/**
 * Store for captured URL parameters that need to be forwarded to client
 * Persists the code across page reloads so it's not lost during the game
 */
export const useUrlParamsStore = defineStore(`url_params_${BRDGE_PROJECT_ID}`, {
  state: (): {
    capturedCode: string | null;
    capturedRedirectUrl: string | null;
    capturedScore: number | null;
    capturedMaxScore: number | null;
  } => ({
    capturedCode: null,
    capturedRedirectUrl: null,
    capturedScore: null,
    capturedMaxScore: null,
  }),
  actions: {
    /**
     * Capture all URL params on initial load.
     * - `v` is stored as capturedCode (for void check) and mapped to utm_source in the outgoing URL.
     * - All other params are forwarded as-is.
     * Should be called once when the app starts.
     */
    captureFromUrl() {
      if (typeof window === "undefined") return;

      const params = new URLSearchParams(window.location.search);
      const code = params.get(FEATURES.redirect.incomingParamName);

      if (code) {
        this.capturedCode = code;
      }

      // Build the outgoing redirect URL:
      // rename `v` → `utm_source`, pass all other params through.
      const outgoing = new URLSearchParams();
      outgoing.set(FEATURES.redirect.outgoingParamName, code ?? "");
      params.forEach((value, key) => {
        if (key !== FEATURES.redirect.incomingParamName) {
          outgoing.set(key, value);
        }
      });
      const anchor = FEATURES.redirect.clientAnchor
        ? `#${FEATURES.redirect.clientAnchor}`
        : "";
      this.capturedRedirectUrl = `${FEATURES.redirect.clientBaseUrl}?${outgoing.toString()}${anchor}`;
    },

    /**
     * Persist the final score so the solved page can display it
     */
    captureScore(score: number, maxScore: number) {
      this.capturedScore = score;
      this.capturedMaxScore = maxScore;
    },

    /**
     * Whether the captured code represents a "void" (no external contest)
     */
    isVoid(): boolean {
      return this.capturedCode === FEATURES.redirect.voidValue;
    },

    /**
     * Get the full redirect URL (pre-built at capture time)
     */
    getRedirectUrl(): string {
      return this.capturedRedirectUrl ?? FEATURES.redirect.clientBaseUrl;
    },

    /**
     * Clear all captured state (e.g., on reset)
     */
    clear() {
      this.capturedCode = null;
      this.capturedRedirectUrl = null;
      this.capturedScore = null;
      this.capturedMaxScore = null;
    },
  },
  persist: true,
});
